/**
 * Page affichée quand l'AuthGuard reçoit 403 :
 *   - code === 'tenant_suspended_payment' → CTAs paiement (CB instant / SEPA)
 *   - code === 'tenant_suspended'         → "compte désactivé"
 *   - code === 'tenant_resilie'           → "compte résilié"
 *
 * Mode SEPA + CB de secours :
 *   - Si l'utilisateur a une CB de secours → bouton "Payer par CB" (instant)
 *   - Sinon → bouton "Ajouter une CB de secours" (Stripe Checkout setup)
 *   - Toujours dispo : bouton "Représenter le SEPA" (J+5, débloque par confiance)
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";

export function AccountBlockedPage() {
  const [code, setCode] = useState<string>("tenant_suspended");

  useEffect(() => {
    const c = sessionStorage.getItem("victor.blocked_code");
    if (c) setCode(c);
  }, []);

  const isPayment = code === "tenant_suspended_payment";
  const isResilie = code === "tenant_resilie";

  const pmQ = useQuery({
    queryKey: ["payment-methods"],
    queryFn: () => api.abonnementSaas.getPaymentMethods(),
    enabled: isPayment,
  });

  const payNowMut = useMutation({
    mutationFn: (via: "card" | "sepa") => api.abonnementSaas.payNow(via),
    onSuccess: (r) => {
      if (r.status === "paid" || r.status === "processing") {
        sessionStorage.removeItem("victor.blocked_code");
        setTimeout(() => window.location.replace("/"), 1500);
      } else if (r.hostedInvoiceUrl) {
        window.location.href = r.hostedInvoiceUrl;
      }
    },
  });

  const backupCardMut = useMutation({
    mutationFn: () => api.abonnementSaas.setupBackupCard(),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-50 p-6">
      <div className="max-w-xl w-full rounded-2xl bg-white p-8 ring-1 ring-navy-100 shadow-sm space-y-5">
        {isPayment ? (
          <>
            <header>
              <h1 className="text-2xl font-bold text-navy-800">
                Paiement requis
              </h1>
              <p className="mt-2 text-navy-600">
                Votre dernier prélèvement n'a pas pu être encaissé. Choisissez
                comment régulariser pour rouvrir votre accès.
              </p>
            </header>

            {payNowMut.data?.status === "paid" ? (
              <p className="rounded-xl bg-emerald-50 px-4 py-3 ring-1 ring-emerald-200 text-sm text-emerald-800">
                Paiement reçu. Réouverture de votre session…
              </p>
            ) : payNowMut.data?.status === "processing" ? (
              <p className="rounded-xl bg-emerald-50 px-4 py-3 ring-1 ring-emerald-200 text-sm text-emerald-800">
                Prélèvement SEPA initié. Votre accès est rétabli. Si le
                prélèvement est rejeté par votre banque, l'accès sera de
                nouveau suspendu.
              </p>
            ) : (
              <div className="space-y-3">
                {/* CTA 1 : CB instant */}
                <div className="rounded-xl bg-navy-50/60 p-4 ring-1 ring-navy-100">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-navy-500">
                    Rétablir immédiatement
                  </h3>
                  {pmQ.data?.hasBackupCard ? (
                    <>
                      <p className="mt-1 text-sm text-navy-600">
                        Carte de secours :{" "}
                        <strong>
                          {(pmQ.data.backupCardBrand ?? "CB").toUpperCase()} ····{" "}
                          {pmQ.data.backupCardLast4 ?? "????"}
                        </strong>{" "}
                        · prélèvement instantané.
                      </p>
                      <button
                        type="button"
                        className="mt-3 w-full rounded-xl bg-accent-500 px-4 py-2 font-bold text-white hover:bg-accent-600 disabled:opacity-60"
                        onClick={() => payNowMut.mutate("card")}
                        disabled={payNowMut.isPending}
                      >
                        {payNowMut.isPending && payNowMut.variables === "card"
                          ? "Paiement…"
                          : "Payer par carte de secours"}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="mt-1 text-sm text-navy-600">
                        Aucune carte de secours enregistrée. Ajoutez-en une
                        pour pouvoir débloquer instantanément en cas d'impayé
                        SEPA.
                      </p>
                      <button
                        type="button"
                        className="mt-3 w-full rounded-xl bg-accent-500 px-4 py-2 font-bold text-white hover:bg-accent-600 disabled:opacity-60"
                        onClick={() => backupCardMut.mutate()}
                        disabled={backupCardMut.isPending}
                      >
                        {backupCardMut.isPending
                          ? "Redirection…"
                          : "Ajouter une carte de secours"}
                      </button>
                    </>
                  )}
                </div>

                {/* CTA 2 : SEPA */}
                <div className="rounded-xl bg-navy-50/60 p-4 ring-1 ring-navy-100">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-navy-500">
                    Représenter le prélèvement SEPA
                  </h3>
                  <p className="mt-1 text-sm text-navy-600">
                    Stripe relance le prélèvement sur votre compte habituel.
                    Délai : 2 à 5 jours ouvrés. Votre accès est rétabli
                    immédiatement par confiance.
                  </p>
                  <button
                    type="button"
                    className="mt-3 w-full rounded-xl bg-white px-4 py-2 font-bold text-navy-700 ring-1 ring-navy-200 hover:bg-navy-50 disabled:opacity-60"
                    onClick={() => payNowMut.mutate("sepa")}
                    disabled={payNowMut.isPending}
                  >
                    {payNowMut.isPending && payNowMut.variables === "sepa"
                      ? "Initiation…"
                      : "Représenter le SEPA"}
                  </button>
                </div>

                <a
                  href="/abonnement"
                  className="block text-center text-xs font-bold text-accent-500 hover:underline"
                >
                  Gérer mon abonnement
                </a>
              </div>
            )}

            {payNowMut.error || backupCardMut.error ? (
              <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
                {((payNowMut.error || backupCardMut.error) as Error).message}
              </p>
            ) : null}
          </>
        ) : isResilie ? (
          <>
            <h1 className="text-2xl font-bold text-navy-800">
              Compte résilié
            </h1>
            <p className="text-navy-600">
              Ce compte a été résilié. Pour toute question, contactez Hubblot.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-navy-800">
              Compte désactivé
            </h1>
            <p className="text-navy-600">
              L'accès à votre compte Victor a été temporairement suspendu.
              Contactez Hubblot pour plus d'informations.
            </p>
            <a
              href="mailto:hello@hubblot.fr"
              className="inline-block text-sm font-bold text-accent-500 hover:underline"
            >
              hello@hubblot.fr
            </a>
          </>
        )}
      </div>
    </div>
  );
}
